import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getPaginatedUserWalletBalances } from '../../../services/AccountService';

const UserWalletBalances = () => {
    const partnerId = useSelector((state) => state.auth.authUser?.partner_id);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const { data, isFetching, isError } = useQuery({
        queryKey: ['userWalletBalances', partnerId, page, debouncedSearch],
        queryFn: () => getPaginatedUserWalletBalances(partnerId, page, debouncedSearch).then(res => res.data),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!partnerId,
    });

    const users     = data?.data         ?? [];
    const lastPage  = data?.last_page    ?? 1;
    const total     = data?.total        ?? 0;

    return (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
                        <h2 className="heading mb-0">Total Wallet Balances</h2>
                        {total > 0 && (
                            <span className="text-muted small">{total} user{total !== 1 ? 's' : ''}</span>
                        )}
                    </div>
                    <div className="card-body pt-3">
                        <div className="mb-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        {isFetching && users.length === 0 ? (
                            <div className="text-center py-4">
                                <span className="spinner-border spinner-border-sm text-primary" role="status" />
                            </div>
                        ) : isError ? (
                            <p className="text-center text-danger py-4 mb-0">
                                Failed to load user wallet balances. Please try again.
                            </p>
                        ) : users.length === 0 ? (
                            <p className="text-center text-muted py-4 mb-0">
                                {debouncedSearch ? `No users matching "${debouncedSearch}".` : 'No user wallets found.'}
                            </p>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>User</th>
                                                <th className="text-end">Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user, index) => (
                                                <tr key={user.user_unique_id}>
                                                    <td className="text-muted">
                                                        {(page - 1) * 10 + index + 1}
                                                    </td>
                                                    <td>
                                                        <div
                                                            title={user.userName}
                                                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                                                        >
                                                            {user.userName}
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-semibold text-nowrap">{user.balance}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {lastPage > 1 && (
                                    <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link p-0 text-decoration-none"
                                            disabled={page === 1}
                                            onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                            aria-label="Previous page"
                                        >
                                            <i className="fa fa-angle-left me-1" />
                                            Prev
                                        </button>
                                        <span className="text-muted small">
                                            Page {page} of {lastPage}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link p-0 text-decoration-none"
                                            disabled={page === lastPage}
                                            onClick={() => setPage((p) => Math.min(p + 1, lastPage))}
                                            aria-label="Next page"
                                        >
                                            Next
                                            <i className="fa fa-angle-right ms-1" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserWalletBalances;
